<script>
import ModelEdit from '../models/edit-v2';
import FormsModel from "@/components/forms/purchase-order-edit";
// eslint-disable-next-line no-unused-vars
import {serialize} from 'object-to-formdata'
import FailedToFetchModal from "@/router/views/purchase_orders/modals/FailedToFetchModal.vue";
import FailedToUpdateModal from "@/router/views/purchase_orders/modals/FailedToUpdateModal.vue";
import TimeoutRequestModal from "@/router/views/purchase_orders/modals/TimeoutRequestModal.vue";
import SuccessToUpdateModal from "@/router/views/purchase_orders/modals/SuccessToUpdateModal.vue";

export default {
  components: {FailedToUpdateModal, TimeoutRequestModal, FailedToFetchModal, SuccessToUpdateModal},
  extends: ModelEdit,
  page: {
    title: "Edit Purchase Orders",
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t('purchase_orders.singular'))
      }
    },
    resourceRoute: {default: 'purchase_orders'},
    modelVariable: {default: 'purchase_order'},
    form: {
      default() {
        return FormsModel;
      }
    },
    denyDelete: {type: Boolean, default: () => true},
  },
  data() {
    return {
      title: this.lodash.upperFirst(this.$t('purchase_orders.plural')),
      failedToFetchModalShow: false,
      failedToUpdateModalShow: false,
      timeoutRequestModalShow: false,
      successToUpdateModalShow: false,
    }
  },
  mounted() {
    this.fetchModel();
  },
  computed: {
    isEditAllowed() {
      return this.canEditModel(this.model);
    },
    items() {
      return [
        {
          text: this.lodash.upperFirst(this.$t('purchase_orders.plural')),
          href: "/purchase_orders",
        },
        {
          text: this.model && this.model.id ? this.model['name'] : this.$route.params.key,
          href: `/purchase_orders/${this.$route.params.key}`,
          active: true,
        },
        {
          text: this.$t('Edit'),
          href: `/purchase_orders/${this.$route.params.key}/edit`,
          active: true,
        },
      ]
    }
  },
  methods: {
    canEditModel() {
      const hasPermission = this.$auth.hasPermissionTo('purchase_orders.edit');
      return hasPermission;
    },
    fetchModel() {
      this.loading = true;
      this.$axios.get(`/purchase_orders/${this.$route.params.key}`, {
        params: {
          with: [
            "feed_products",
            'files',
            'hatchery',
            'paymentables',
            'purchase_items.trip_items',
            'purchase_additional_items',
            'purchase_operational_costs',
            'product',
            'ticket',
            'ticketable',
            'purchase_plan.sales_plan',
            'farmer',
            'farm',
            'customer',
            'odoo_purchase_order',
            'creator',
          ].join(','),
        },
        timeout: 30000,
      }).then((response) => {
        const {data: {data}} = response;
        let files = data.files.map((v) => {
          return {
            ...v,
            preview: v.url,
            format: v.extension
          }
        })
        this.model = {
          is_paid: data.total_nominal !== 0,
          is_stock_used: data.purchase_item_total_available_quantity < data.purchase_item_total_quantity,
          is_fully_billed: data.odoo_purchase_order?.invoice_status === 'invoiced',
          purchase_additional_fees: (data.purchase_additional_items || []).filter(v => v.total >= 0),
          purchase_discounts: (data.purchase_additional_items || []).filter(v => v.total < 0).map(v => ({
            ...v,
            total: Math.abs(v.total)
          })),
          ...data,
          doc: data.purchase_items?.[0]?.doc,
          feed_product_ids: data.feed_products.map(({id}) => id),
          files: data.files.map((v) => {
            let file = {...v, preview: v.url, format: v.extension}
            delete file.path

            return file
          }),
          shrimp_origin: data.purchase_items?.[0]?.shrimp_origin,
          initial_product_id: data.product_id,
          down_payment_document: files.find(v => v.name === 'down payment document') || {name: 'down payment document'},
          invoice_document: files.find(v => v.name !== 'down payment document') || {name: 'invoice document'},
          invoice_documents: files.filter(v => v.name === 'invoice document') || [{name: 'invoice document'}],
          down_payment_documents: files.filter(v => v.name === 'down payment document') || [{name: 'down payment document'}],
          operational_cost_documents: files.filter(({name}) => name === 'operational cost document') || {name: 'operational cost document'},
          mark_up: data.product.code === 'harvest'
              ? (data.markup_kg ?? (data.total_operational_cost / data.purchase_item_total_quantity))
              : 0,
        };
        if (!this.canEditModel(this.model)) {
          window.location.href = `/purchase_orders/${this.$route.params.key}`;
          return response;
        }

        return response;
      }).catch((error) => {
        if (error.code === 'ECONNABORTED') {
          this.showTimeoutRequestModal()
        } else {
          this.showFailedToFetchModal()
        }
      })
          .finally(() => this.loading = false)
    },
    update(value) {
      let purchaseOrder = this.requestPayload(value)

      return this.$axios.post(`/purchase_orders/${this.$route.params.key}`, (
          serialize({...purchaseOrder, _method: 'PUT'}, {indices: true, booleansAsIntegers: true})
      ), {timeout: 30000}).then(() => {
        localStorage.removeItem('purchase_order')
      })
    },
    handleUpdate(value) {
      this.updating = true;
      this.update(value)
          .then(() => {
            this.showSuccessToUpdateModal()
            this.isEditing = false;
            setTimeout(() => this.redirectToDetailPage(), 2000)
          })
          .catch(() => this.showFailedToUpdateModal())
          .finally(() => {
            this.updating = false;
          });
    },
    requestPayload(value) {
      let detachFeedProductIds = value.feed_products
          .filter(({id}) => !value.feed_product_ids.includes(id))
          .map(({id}) => ({id, _action: 'delete'}))
      let addFeedProductIds = value.feed_product_ids.map((val) => ({id: val}))
      const isHarvest = value.type === 'harvest' || value.shrimp_selected?.code === 'harvest'
      if (value.active_form_component === 'edit-form-after-payment') {
        return {
          hatchery_id: value.hatchery_id,
          feed_products: detachFeedProductIds.concat(addFeedProductIds),
          feed: value.feed,
          odoo_purchase_order: value.odoo_purchase_order,
          purchase_items: value.purchase_items,
          ticketable: value.ticketable,
          duration: value.duration,
          total_operational_cost: value.total_operational_cost,
          description: value.description,
          ...(isHarvest ? {markup_kg: value.mark_up} : {}),
        }
      }

      let payload = {
        ...value,
        farm_id: value.farm_id ?? null,
        feed_products: detachFeedProductIds.concat(addFeedProductIds),
        files: value.files.filter(v => typeof v.file !== 'string').map(v => ({...v, path: v.file})),
        purchase_items: value.purchase_items.map(v => {
          return {
            ...v,
            shrimp_origin: value.shrimp_origin,
          }
        }),
        purchase_additional_items: value.additional_fees.concat(value.discounts.map(v => ({...v, total: -v.total}))),
        odoo_purchase_order: {
          ...value.odoo_purchase_order,
          odoo_vendor_id: value.vendor_selection === 'supplier' ? value.customer.odoo_partner_id : value.farmer.odoo_partner_id,
          vendor_selection: value.vendor_selection,
        }
      }
      if (isHarvest) {
        payload.markup_kg = payload.mark_up
        delete payload.mark_up
      }
      let operationalCosts = value.other_operational_costs.map(v => ({...v, amount: v.total,}))
      let iceCost = {
        ...value.operational_costs.find(({name}) => name.toLowerCase() === 'ice'),
        name: 'ice',
        amount: value.ice_cost,
      }
      let labourCost = {
        ...value.operational_costs.find(({name}) => name.toLowerCase() === 'labour'),
        name: 'labour',
        amount: value.labour_cost,
      }
      let logisticCost = {
        ...value.operational_costs.find(({name}) => name.toLowerCase() === 'delivery'),
        name: 'delivery',
        amount: value.shipping_cost,
      }
      payload.purchase_operational_costs = operationalCosts.concat([iceCost, labourCost, logisticCost])

      if (payload.type === 'down_payment') {
        delete payload.purchase_items;
      }
      delete payload.additional_fees
      delete payload.discounts

      if (typeof payload.file === 'string') {
        delete payload.file;
      }

      const files = [];

      if (payload.invoice_documents) {
        payload.invoice_documents.map((document) => {
          files.push({...document, name: 'invoice document', path: document.file})
        })
      }
      if (payload.down_payment_documents) {
        payload.down_payment_documents.map((document) => {
          files.push({...document, name: 'down payment document', path: document.file})
        })
      }
      if (payload.operational_cost_documents) {
        payload.operational_cost_documents.map((document) => {
          files.push(({...document, name: 'operational cost document', path: document.file}))
        })
      }
      payload.files = files;
      if (value.type === 'harvest') {
        payload.total_operational_cost = value.total_operational_cost;
        delete payload.operational_costs;

      }
      return payload
    },
    delete() {
      return this.$axios.delete(`/purchase_orders/${this.$route.params.key}`)
    }, showFailedToFetchModal() {
      this.$set(this, 'failedToFetchModalShow', true);
    },
    closeFailedToFetchModal() {
      this.$set(this, 'failedToFetchModalShow', false);
    },
    retryFailedToFetchModal() {
      this.closeFailedToFetchModal()
      this.fetchModel()
    },
    showFailedToUpdateModal() {
      this.$set(this, 'failedToUpdateModalShow', true);
    },
    closeFailedToUpdateModal() {
      this.$set(this, 'failedToUpdateModalShow', false);
    },
    retryFailedToUpdateModal() {
      this.closeFailedToUpdateModal()
      this.handleUpdate(this.model)
    },
    showTimeoutRequestModal() {
      this.$set(this, 'timeoutRequestModalShow', true);
    },
    closeTimeoutRequestModal() {
      this.$set(this, 'timeoutRequestModalShow', false);
    },
    retryTimeoutRequestModal() {
      this.closeTimeoutRequestModal()
      this.fetchModel()
    },
    showSuccessToUpdateModal() {
      this.$set(this, 'successToUpdateModalShow', true);
    },
    redirectToDashboard() {
      this.$router.push('/')
    },
    redirectToDetailPage(){
      this.$router.push(`/purchase_orders/${this.$route.params.key}`)
    }
  }
}
</script>
<template>
  <Layout>
    <PageHeader :title="title" :items="items"/>
    <failed-to-fetch-modal
        v-model="failedToFetchModalShow"
        :name="$t('purchase_orders.singular')"
        :model-id="$route.params.key"
        @back="handleBack"
        @retry="retryFailedToFetchModal"
    />
    <failed-to-update-modal
        v-model="failedToUpdateModalShow"
        :name="$t('purchase_orders.singular')"
        :model-id="$route.params.key"
        @back="handleBack"
        @retry="retryFailedToUpdateModal"
    />
    <success-to-update-modal
        v-model="successToUpdateModalShow"
        :name="$t('purchase_orders.singular')"
        :model-id="$route.params.key"
        @back-to-dashboard="redirectToDashboard"
        @view-detail-page="redirectToDetailPage"
    />
    <timeout-request-modal
        v-model="timeoutRequestModalShow"
        @back="handleBack"
        @retry="retryTimeoutRequestModal"
    />
    <div class="row">
      <model-edit
          class="col-12"
          :loading="loading"
          :updating="updating"
          :deleting="deleting"
          v-bind="$props"
          v-model="model"
          :errors.sync="errors"
          :key="getModelKey()"
          @back-click="handleBack"
          @update-click="handleUpdate"
          @delete-click="handleDelete"
          @model-changed="handleModelChanged">
      </model-edit>
    </div>
  </Layout>
</template>
